import React, { useState } from 'react';
import {
  Box, Flex, Text, Collapse, Icon, VStack, HStack,
} from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FaChevronDown, FaChevronUp, FaClipboardList, FaBoxes,
  FaUsersCog, FaMapMarkedAlt, FaSnowflake,
} from 'react-icons/fa';

const NavItem = ({ to, icon, children, onClose, end = false }) => {
  const location = useLocation();
  const isActive = end
    ? location.pathname === to
    : location.pathname.startsWith(to);

  return (
    <Box
      as={NavLink}
      to={to}
      end={end}
      onClick={() => onClose && onClose()}
      display="flex"
      alignItems="center"
      gap={3}
      px={3}
      py={2.5}
      borderRadius="lg"
      fontSize="sm"
      fontWeight={isActive ? '600' : '500'}
      color={isActive ? 'white' : 'slate.400'}
      bg={isActive ? 'brand.600' : 'transparent'}
      _hover={{
        bg: isActive ? 'brand.600' : 'whiteAlpha.100',
        color: isActive ? 'white' : 'slate.200',
        textDecoration: 'none',
      }}
      transition="all 0.2s"
    >
      <Icon as={icon} boxSize={4} opacity={isActive ? 1 : 0.85} />
      <Text>{children}</Text>
    </Box>
  );
};

const NavGroup = ({ label, icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const location = useLocation();
  const isChildActive = ['/technicians'].some((p) => location.pathname.startsWith(p));

  return (
    <Box>
      <Flex
        align="center"
        justify="space-between"
        px={3}
        py={2.5}
        borderRadius="lg"
        cursor="pointer"
        color={isChildActive ? 'white' : 'slate.400'}
        bg={isChildActive && !open ? 'whiteAlpha.100' : 'transparent'}
        _hover={{ bg: 'whiteAlpha.100', color: 'slate.200' }}
        onClick={() => setOpen(!open)}
        transition="all 0.2s"
      >
        <HStack spacing={3}>
          <Icon as={icon} boxSize={4} />
          <Text fontSize="sm" fontWeight="500">{label}</Text>
        </HStack>
        <Icon as={open ? FaChevronUp : FaChevronDown} boxSize={3} />
      </Flex>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" pl={4} mt={1} spacing={0.5}>
          {children}
        </VStack>
      </Collapse>
    </Box>
  );
};

const SubNavItem = ({ to, children, onClose }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Box
      as={NavLink}
      to={to}
      onClick={() => onClose && onClose()}
      px={3}
      py={2}
      borderRadius="md"
      fontSize="sm"
      color={isActive ? 'brand.200' : 'slate.500'}
      fontWeight={isActive ? '600' : '400'}
      borderLeft="2px solid"
      borderColor={isActive ? 'brand.400' : 'transparent'}
      _hover={{ color: 'slate.200', textDecoration: 'none' }}
    >
      {children}
    </Box>
  );
};

const Sidebar = ({ onClose }) => (
  <Box
    w="260px"
    h="full"
    bg="slate.900"
    display="flex"
    flexDirection="column"
    overflow="hidden"
  >
    <Box px={5} py={5} borderBottom="1px solid" borderColor="whiteAlpha.100">
      <HStack spacing={3}>
        <Flex
          w={9}
          h={9}
          borderRadius="lg"
          bg="brand.500"
          align="center"
          justify="center"
          boxShadow="0 0 20px rgba(8, 145, 178, 0.4)"
        >
          <Icon as={FaSnowflake} color="white" boxSize={4} />
        </Flex>
        <Box>
          <Text color="white" fontWeight="700" fontSize="md" fontFamily="heading" lineHeight="1.2">
            TerraCool
          </Text>
          <Text color="slate.500" fontSize="xs">Admin Console</Text>
        </Box>
      </HStack>
    </Box>

    <VStack align="stretch" flex={1} px={3} py={4} spacing={1} overflowY="auto">
      <Text px={3} mb={1} fontSize="xs" fontWeight="700" color="slate.600" letterSpacing="0.08em">
        OPERATIONS
      </Text>
      <NavItem to="/complaints" icon={FaClipboardList} onClose={onClose}>
        Complaints
      </NavItem>
      <NavItem to="/inventory" icon={FaBoxes} onClose={onClose}>
        Inventory
      </NavItem>

      <Text px={3} mt={4} mb={1} fontSize="xs" fontWeight="700" color="slate.600" letterSpacing="0.08em">
        FIELD TEAM
      </Text>
      <NavGroup label="Technicians" icon={FaUsersCog} defaultOpen>
        <SubNavItem to="/technicians/technician-list" onClose={onClose}>
          Technician List
        </SubNavItem>
        <SubNavItem to="/technicians/live-tracking" onClose={onClose}>
          Live Tracking
        </SubNavItem>
      </NavGroup>
    </VStack>

    <Box px={4} py={4} borderTop="1px solid" borderColor="whiteAlpha.100">
      <Box
        p={3}
        borderRadius="xl"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Text fontSize="xs" color="slate.500" mb={0.5}>Terracool Services</Text>
        <Text fontSize="sm" color="slate.300" fontWeight="500">HVAC Operations Hub</Text>
      </Box>
    </Box>
  </Box>
);

export default Sidebar;
